// Hook TanStack Query — sante du parc (GET /api/parc/sante), rafraichie toutes les 5 minutes.
//
// FORME : recopiee du contrat serveur `apps/admin/server/services/parcSante/contrat.ts`,
// qui la VALIDE par Zod a la sortie. Recopiee et non importee : le projet client a
// `rootDir: ./src` et ne peut pas inclure un fichier serveur, meme pour un type. Un champ
// ajoute au contrat s ajoute ici.
//
// Cle de cache SANS companyId, et c est voulu (ADR-032) : c est la telemetrie de la
// PLATEFORME, reservee a l administrateur d organisation — aucune donnee de compagnie.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { api } from '../services/api';

export type MotifCode =
  | 'acces-refuse'
  | 'delai'
  | 'configuration'
  | 'identite'
  | 'reponse-invalide'
  | 'erreur-azure';
export interface Motif {
  code: MotifCode;
  http?: number;
}
interface Illisible {
  etat: 'illisible';
  motif: Motif;
}

export interface Repond {
  etat: 'ok';
  repond: boolean;
  httpStatus: number | null;
  dureeMs: number;
  demarreeIlYaS: number | null;
  version: string | null;
  erreur: string | null;
}

export interface Bruit {
  total: number;
  entrees: { id: string; source: 'exceptions' | 'dependances-en-echec'; echarde: string; n: number }[];
}

export interface Heure {
  h: string;
  n: number;
  e: number;
}

export type Telemetrie =
  | {
      etat: 'ok';
      requetes: number;
      erreurs5xx: number;
      refus4xx: number;
      p95Ms: number | null;
      lentes: number;
      exceptions: number;
      bruit: Bruit;
      heures: Heure[];
    }
  | { etat: 'non-transmis'; exceptions: number; bruit: Bruit }
  | Illisible;

export const GENRES = ['silence', 'erreurs', 'disponibilite', 'redemarrages', 'base', 'cloisonnement'] as const;
export type Genre = (typeof GENRES)[number];

export type Couverture =
  | { etat: 'voit' }
  | { etat: 'rarement'; raison: 'volume' | 'volume-sql'; n: number; total: number }
  | {
      etat: 'aveugle';
      raison: 'sans-signal' | 'sans-requetes' | 'volume' | 'volume-sql' | 'sans-demarrage' | 'sans-sql';
    }
  | { etat: 'inconnue' }
  | { etat: 'sans-objet' };

export interface Alarme {
  genre: Genre;
  regle: string | null;
  couverture: Couverture;
  declenchements: string[];
}

export type Statut = 'ok' | 'erreurs' | 'lente' | 'illisible' | 'ne-repond-pas';

export interface AppSante {
  id: string;
  ressource: string | null;
  statut: Statut;
  repond: Repond;
  telemetrie: Telemetrie;
  alarmes: { etat: 'ok'; liste: Alarme[] } | Illisible;
}

export interface SanteDuParc {
  mesureA: string;
  apps: AppSante[];
  declenchements: { etat: 'ok'; h24: number; j30: number } | Illisible;
}

export const PARC_SANTE_QUERY_KEY = ['parc-sante'] as const;
const CINQ_MINUTES_MS = 5 * 60_000;

export function useParcSante() {
  const { user } = useAuth();
  return useQuery({
    queryKey: PARC_SANTE_QUERY_KEY,
    queryFn: () => api.get<SanteDuParc>('/api/parc/sante'),
    enabled: !!user,
    refetchInterval: CINQ_MINUTES_MS,
    staleTime: CINQ_MINUTES_MS,
  });
}

/** « Remesurer » : le serveur ne relance une vraie mesure que si la sienne a plus d une minute. */
export function useRemesurerParcSante() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.get<SanteDuParc>('/api/parc/sante?remesurer=1'),
    onSuccess: (donnees) => client.setQueryData(PARC_SANTE_QUERY_KEY, donnees),
  });
}
